'use client';

import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';
import { checkSlugAvailability } from '@/services/webApi';
import { normalizeSlug, isValidSlugFormat } from '@goodneighbor/utilities/slug';
import type { CheckSlugResponse } from '@goodneighbor/api/routes/organizations/checkSlug/checkSlug.route';

export type SlugStatus =
  | 'idle'
  | 'checking'
  | 'available'
  | 'taken'
  | 'invalid';

const DEBOUNCE_MS = 400;
const MIN_SLUG_LENGTH = 3;

export function useSlugAvailability(initialSlug = '') {
  const [slug, setSlugState] = useState(initialSlug);
  const [debouncedSlug] = useDebounce(slug, DEBOUNCE_MS);

  const isValid = slug.length >= MIN_SLUG_LENGTH && isValidSlugFormat(slug);
  const shouldCheck =
    isValid &&
    debouncedSlug.length >= MIN_SLUG_LENGTH &&
    isValidSlugFormat(debouncedSlug);

  const { data, isFetching, isError } = useQuery<CheckSlugResponse>({
    queryKey: ['slug-availability', debouncedSlug],
    queryFn: () => checkSlugAvailability(debouncedSlug),
    enabled: shouldCheck,
    staleTime: 30000,
    retry: false
  });

  const status = useMemo((): SlugStatus => {
    if (slug.length < MIN_SLUG_LENGTH) return 'idle';
    if (!isValidSlugFormat(slug)) return 'invalid';
    if (slug !== debouncedSlug || isFetching) return 'checking';
    if (isError) return 'taken';
    if (data) return data.data.available ? 'available' : 'taken';
    return 'checking';
  }, [slug, debouncedSlug, isFetching, isError, data]);

  const setSlug = useCallback((value: string) => {
    setSlugState(normalizeSlug(value, { keepTrailingHyphen: true }));
  }, []);

  return {
    slug,
    setSlug,
    status,
    isChecking: status === 'checking',
    isAvailable: status === 'available',
    isTaken: status === 'taken',
    isInvalid: status === 'invalid'
  };
}
